import {
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
  TypographyVariant,
} from "@mui/material";
import { classes } from "portal/theme/theme";
import { isUndefined } from "portal/utils/identity";
import { portalApi } from "portal/state/portalApi";
import { RobotSummaryResponse } from "protos/portal/robots";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useTranslation } from "react-i18next";
import { valueOrLoading } from "portal/utils/strings";
import { WithSkeleton } from "../WithSkeleton";
import DropdownIcon from "@mui/icons-material/ArrowDropDownOutlined";
import React, { FunctionComponent, useState } from "react";

interface Props {
  serial?: string;
  customerSerial?: string;
  typography: TypographyVariant;
  typographyClassName?: string;
  onChange: (summary: RobotSummaryResponse) => void;
}

export const RobotSelector: FunctionComponent<Props> = ({
  serial,
  customerSerial,
  typography = "body1",
  typographyClassName,
  onChange,
}) => {
  const { t } = useTranslation();

  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | undefined>();
  const { data: summaries, isLoading } = useQueryPopups(
    portalApi.endpoints.listRobots.useQuery({
      withAlarms: false,
      excludeCustomer: true,
      excludeMetrics: true,
    }),
    {
      errorVariant: "warning",
    }
  );

  return (
    <WithSkeleton
      success={!isUndefined(serial)}
      variant="rectangular"
      className="min-h-6 min-w-10 mr-4 flex-shrink-0"
    >
      <>
        <Button
          onClick={
            isLoading
              ? undefined
              : (event) => setMenuAnchor(event.currentTarget)
          }
          variant="text"
          className="text-white normal-case"
          endIcon={
            isLoading ? (
              <CircularProgress color="info" className="w-3 h-3" />
            ) : (
              <DropdownIcon />
            )
          }
        >
          <Typography
            variant={typography}
            component="div"
            className={classes(
              "whitespace-nowrap text-inherit",
              typographyClassName
            )}
          >
            {valueOrLoading(customerSerial, t)}
          </Typography>
        </Button>
        <Menu
          onClose={() => setMenuAnchor(undefined)}
          open={!isUndefined(menuAnchor)}
          anchorEl={menuAnchor}
          className="max-h-[95%]"
        >
          {summaries?.map((summary, index) => (
            <MenuItem
              value={summary.robot?.serial}
              key={summary.robot?.serial ?? `robot-${index}`}
              className={summary.robot?.serial === serial ? "hidden" : ""}
              onClick={() => {
                setMenuAnchor(undefined);
                onChange(summary);
              }}
            >
              {valueOrLoading(summary.robot?.customerSerial, t)}
            </MenuItem>
          ))}
        </Menu>
      </>
    </WithSkeleton>
  );
};
